import {
  CubeSideData,
  UseCategoryPageDataArgs,
  UseCategoryPageDataReturn,
} from "./types";
import {
  getItemizedAverageScoreData,
  getConsentItemsDistribution,
  getQualityImportanceItems,
} from "./utils";
import { CalculationType } from "../../types";
import { CubeSideName } from "../../components/Сube/types";
import { CUBE_SIDE_NAMES } from "../../components/Сube/constants";
import {
  useCategoryData,
  UseCategoryDataReturn,
} from "../../hooks/useHomePageData";
import { DtoTestSet } from "@/types/dto-types";

/**
 * Creates default cube side data with zero values
 */
function createDefaultCubeSideData(cubeSideName: CubeSideName): CubeSideData {
  return {
    // Basic information
    title: `Side ${cubeSideName}`,
    description: `Default description for side ${cubeSideName}`,

    // Calculation type data
    [CalculationType.Benchmark]: {
      percentageValue: 0,
      cubeSideFillArea: 0,
    },
    [CalculationType.AverageScore]: {
      percentageValue: 0,
      cubeSideFillArea: 0,
    },

    // Score metrics
    score: 0,
    benchmark: 0,
    scoreVsBenchmarkDifference: 0,

    // Processed data
    qualityImportanceItems: [],
    participationQuarterAreaItems: {
      "strong-rejection": 0,
      "weak-rejection": 0,
      "weak-consent": 0,
      "strong-consent": 0,
    },
    itemizedAverageScoreData: [],
  };
}

function ensureCompleteCubeSidesData(
  cubeSidesData?: Partial<Record<CubeSideName, CubeSideData>>
): Record<CubeSideName, CubeSideData> {
  const completeCubeSidesData = {} as Record<CubeSideName, CubeSideData>;

  CUBE_SIDE_NAMES.forEach(sideName => {
    completeCubeSidesData[sideName] =
      cubeSidesData?.[sideName] || createDefaultCubeSideData(sideName);
  });

  return completeCubeSidesData;
}

function mapCubeSidesData(
  testSets: DtoTestSet[],
  { categoryCubeOverviewData, categoryValuesPerSide }: UseCategoryDataReturn
): Record<CubeSideName, CubeSideData> {
  return testSets.reduce((acc, testSet) => {
    const cubeSideName = testSet.info.cubeSide as CubeSideName;
    const sideData = categoryValuesPerSide[cubeSideName];

    acc[cubeSideName] = {
      title: testSet.info.name,
      description: testSet.info.infoCockpit,

      // Calculation type data
      [CalculationType.Benchmark]: {
        percentageValue:
          categoryCubeOverviewData[CalculationType.Benchmark].percentageValue[
            cubeSideName
          ],
        cubeSideFillArea:
          categoryCubeOverviewData[CalculationType.Benchmark].cubeSideFillArea[
            cubeSideName
          ],
      },
      [CalculationType.AverageScore]: {
        percentageValue:
          categoryCubeOverviewData[CalculationType.AverageScore]
            .percentageValue[cubeSideName],
        cubeSideFillArea:
          categoryCubeOverviewData[CalculationType.AverageScore]
            .cubeSideFillArea[cubeSideName],
      },

      // Score metrics
      score: sideData.averagePercentageScore,
      benchmark: sideData.benchmarkPercentageScore,
      scoreVsBenchmarkDifference:
        sideData.averagePercentageScore - sideData.benchmarkPercentageScore,

      // Processed data
      qualityImportanceItems: getQualityImportanceItems(
        sideData.importanceItems,
        sideData.qualityItems
      ),
      participationQuarterAreaItems: getConsentItemsDistribution(
        sideData.consentItems
      ),
      itemizedAverageScoreData: getItemizedAverageScoreData([
        sideData.aggregatedReport,
      ]),
    };

    return acc;
  }, {} as Record<CubeSideName, CubeSideData>);
}

function aggregateAllSidesData(
  categoryValuesPerSide: UseCategoryDataReturn["categoryValuesPerSide"]
) {
  const allSidesValues = Object.values(categoryValuesPerSide);

  return {
    allConsentItems: allSidesValues.flatMap(side => side.consentItems),
    allImportanceItems: allSidesValues.flatMap(side => side.importanceItems),
    allQualityItems: allSidesValues.flatMap(side => side.qualityItems),
    allReports: allSidesValues.flatMap(side => side.aggregatedReport),
  };
}

/**
 * Main hook for category page data processing and transformation
 */
export function useCategoryPageData(
  dataArgs: UseCategoryPageDataArgs
): UseCategoryPageDataReturn {
  // Fetch category data
  const categoryData = useCategoryData({
    ...dataArgs,
    category: dataArgs.currentCategory,
  });

  const { categoryAverageScore, categoryRawData, categoryValuesPerSide } =
    categoryData;

  // Aggregate data from all cube sides
  const { allConsentItems, allImportanceItems, allQualityItems, allReports } =
    aggregateAllSidesData(categoryValuesPerSide);

  // Process cube sides data with fallback to default values
  const currentTestTestSets = categoryRawData?.testSets.filter(
    ts => ts.testId === dataArgs.currentTestId
  );
  const processedCubeSidesData = currentTestTestSets
    ? mapCubeSidesData(currentTestTestSets, categoryData)
    : undefined;

  return {
    // Main category metrics
    score: categoryAverageScore.averageScore,
    benchmark: categoryAverageScore.averageBenchmark,
    scoreVsBenchmarkDifference:
      categoryAverageScore.averageScore - categoryAverageScore.averageBenchmark,

    // Aggregated data visualizations
    qualityImportanceItems: getQualityImportanceItems(
      allImportanceItems,
      allQualityItems
    ),
    itemizedAverageScoreData: getItemizedAverageScoreData(allReports),
    participationQuarterAreaItems: getConsentItemsDistribution(allConsentItems),

    // Individual cube sides data - always defined
    cubeSidesData: ensureCompleteCubeSidesData(processedCubeSidesData),
  };
}
